"use client";

import { useEffect, useMemo, useState } from "react";
import EmptyState from "../../../../common/EmptyState";
import Pagination from "../../../../common/Pagination";
import { useHistoryManager } from "../../../../history/useHistoryManager";
import { useSwitchingOrgs } from "../../../../organisation/useSwitchingOrgs";
import {
  setAccessPoint,
  setAccessPointId,
} from "../../../../slice/accessOrg-slice";
import { useAppDispatch } from "../../../../slice/hooks";
import { useSimilarProducts } from "../../../../../lib/services/product-service";
import { generateProductUrl } from "../../../../../lib/util";
import { useFormatCurrency } from "../../../../common/format-currency";

const INITIAL_SIMILAR_PRODUCTS_LIMIT = 4;
const NEXT_SIMILAR_PRODUCTS_LIMIT = 12;

const BaseSimilarProducts = ({
  product,
  setProduct,
  token,
  isSwitching,
  setSwitching,
  storePath,
  accessPoints,
  navLocation,
  styles,
  PopUp,
  SimilarProductSkeletonCard,
  usePriceDisplay,
  renderProductMedia,
  paginationVariant,
  skeletonsInNestedGrid = false,
}) => {
  const { formatCurrency } = useFormatCurrency();
  const [currency] = useState("NGN");
  const showVendor = navLocation === "marketplace";
  const [searchkey] = useState(product?.name);
  const dispatch = useAppDispatch();

  const [error, setError] = useState(null);
  const [openProductId, setOpenProductId] = useState(null);
  const { navigateTo } = useHistoryManager(storePath);

  const [state, setState] = useState({
    products: [],
    limit: INITIAL_SIMILAR_PRODUCTS_LIMIT,
    total: 0,
    totalPages: 0,
    currentPage: 1,
    page: 1,
    search: searchkey,
  });

  const queryKey = useMemo(
    () => [
      "feeds",
      state.limit,
      state.page,
      state.search,
      product?.productId,
      product?.accessPointId,
    ],
    [
      state.limit,
      state.page,
      state.search,
      product?.productId,
      product?.accessPointId,
    ],
  );
  const expectedSearch = product?.name || "";
  const isSearchSynced = state.search === expectedSearch;

  const {
    data: feeds,
    isLoading,
    isFetching,
    isRefetching,
  } = useSimilarProducts({
    queryKey,
    product,
    token,
    limit: state.limit,
    page: state.page,
    search: state.search,
    navLocation,
    currency,
    enabled: Boolean(token && state.search && isSearchSynced),
    onError: (err) => {
      setError(err.message);
      console.error("Failed to fetch products:", err);
    },
  });

  useEffect(() => {
    if (feeds && feeds.products) {
      const { products, limit, total, totalPages, currentPage } = feeds;
      setState((prev) => {
        const all = [...(prev.products || []), ...products];
        const unique = Array.from(
          new Map(all.map((p) => [p.productId, p])).values(),
        );
        return {
          ...prev,
          products: unique,
          limit,
          total,
          totalPages,
          currentPage,
        };
      });
    }
  }, [feeds]);

  useEffect(() => {
    setState((prev) => {
      if (prev.search === expectedSearch) return prev;
      return {
        ...prev,
        search: expectedSearch,
        products: [],
        page: 1,
        limit: INITIAL_SIMILAR_PRODUCTS_LIMIT,
        total: 0,
        totalPages: 0,
        currentPage: 1,
      };
    });
  }, [expectedSearch]);

  const {
    startSwitch,
    isSwitching: hookSwitching,
    error: switchError,
  } = useSwitchingOrgs({ token });

  const loadingOrSwitching = Boolean(isLoading || hookSwitching || isSwitching);
  const shouldShowSkeletons = Boolean(
    ((isLoading || isFetching) && state.products.length === 0) ||
      isSwitching ||
      hookSwitching,
  );

  const handleProductClick = (productClicked) => {
    if (productClicked.host?.profileId === product.host?.profileId) {
      if (typeof window !== "undefined") {
        window.scrollTo({ top: 0, behavior: "smooth" });
      }

      setProduct(productClicked);
      const productPath = generateProductUrl(storePath, productClicked);
      const accessPoint = accessPoints?.find(
        (ap) => ap.accessPointId === productClicked.accessPointId,
      );

      navigateTo(
        productPath,
        {
          state: { resetScroll: true },
          urlData: {
            product: productClicked,
            accessPoint: accessPoint,
          },
          onNavigate: (navigate) => {
            if (navigate) {
              setSwitching(false);
              dispatch(setAccessPointId(productClicked.accessPointId));
              dispatch(setAccessPoint(accessPoint));
            }
          },
        },
        false,
      );
      return;
    }

    setOpenProductId(productClicked.productId);
  };

  const yesClick = (productClicked) => {
    setSwitching?.(true);
    startSwitch(productClicked);
  };

  const openPage = (page) => {
    setState((prev) => ({
      ...prev,
      page,
      limit:
        page === 1
          ? INITIAL_SIMILAR_PRODUCTS_LIMIT
          : NEXT_SIMILAR_PRODUCTS_LIMIT,
    }));
  };

  const ProductPrice = ({
    product,
    accessPoint = null,
    recievedPriceOption = null,
  }) => {
    const { productPrice, getDisplayPrice } = usePriceDisplay(
      accessPoint,
      product,
      recievedPriceOption,
    );
    const amount =
      productPrice ?? getDisplayPrice(product) ?? product?.amount ?? 0;
    let formatted;
    try {
      formatted = formatCurrency(amount, product.currency);
    } catch (e) {
      formatted = `${amount}`;
    }
    return (
      <h4
        className={styles.priceLine}
        style={{ fontSize: "1.9rem", fontWeight: 500 }}
      >
        {formatted}
      </h4>
    );
  };

  useEffect(() => {
    const handleDocumentClick = (e) => {
      if (!openProductId) return;
      const openEl = document.querySelector(
        `[data-product-id="${openProductId}"]`,
      );
      if (openEl && openEl.contains(e.target)) return;
      setOpenProductId(null);
    };
    document.addEventListener("mousedown", handleDocumentClick);
    return () => document.removeEventListener("mousedown", handleDocumentClick);
  }, [openProductId]);

  if (
    state.products.length === 0 &&
    !loadingOrSwitching &&
    !error &&
    !isRefetching &&
    !isSwitching
  ) {
    return null;
  }

  if (error || switchError) {
    return (
      <>
        <div
          className={styles.similarProductList}
          style={{ marginBottom: "2.5rem" }}
        >
          <h2>Similar Product in other stores</h2>
        </div>
        <EmptyState>{error || String(switchError)}</EmptyState>
      </>
    );
  }

  const skeletonItems = Array.from({ length: 4 }).map((_, idx) => (
    <SimilarProductSkeletonCard key={idx} />
  ));

  return (
    <div className={styles.similarContainer}>
      <div className={styles.similarProductList}>
        <h2>
          {loadingOrSwitching || isRefetching
            ? "Loading Similar Products ..."
            : "Shop for similar Products"}
        </h2>
      </div>
      <div className={styles.similarProducts}>
        {shouldShowSkeletons
          ? skeletonsInNestedGrid
            ? <div className={styles.similarProducts}>{skeletonItems}</div>
            : skeletonItems
          : null}

        {!loadingOrSwitching &&
          state.products.map((similarProduct) => {
            const hasMedia =
              Array.isArray(similarProduct.media) &&
              similarProduct.media.length > 0;
            const media = similarProduct.media
              ?.map((m) => m)
              .filter(Boolean)
              .map((m) => ({
                mediaUrl: m.mediaUrl,
                mediaThumb: m.mediaThumb,
                mediaName: m.mediaName,
                mediaType: m.mediaType,
                mediaExt: m.mediaExt,
                mediaWidth: m.mediaWidth,
                mediaHeight: m.mediaHeight,
                mediaTime: m.mediaTime,
                _id: m._id,
              }));

            return (
              <div
                key={similarProduct.productId}
                data-product-id={similarProduct.productId}
                onClick={() => handleProductClick(similarProduct)}
                className={styles.similarProductItem}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ")
                    handleProductClick(similarProduct);
                }}
              >
                {renderProductMedia({
                  product: similarProduct,
                  media,
                  hasMedia,
                })}

                <div className={styles.itemInfo}>
                  <h4>
                    <p className={styles.names}>{similarProduct.name}</p>
                  </h4>

                  <ProductPrice
                    product={similarProduct}
                    accessPoint={null}
                    recievedPriceOption={similarProduct?.storeFrontDisplayPrice}
                  />

                  {showVendor && similarProduct.host?.name && (
                    <h4>
                      <span>Sold by</span>{" "}
                      <p className={styles.names}>
                        {similarProduct.host.name}
                      </p>
                    </h4>
                  )}
                </div>

                <PopUp
                  product={similarProduct}
                  openProductId={openProductId}
                  setOpenProductId={setOpenProductId}
                  yesClick={yesClick}
                />
              </div>
            );
          })}
      </div>
      {(!isSwitching || !hookSwitching) && (
        <Pagination
          variant={paginationVariant}
          infiniteScroll
          total={state.total}
          limit={state.limit}
          page={state.page}
          onPageClick={openPage}
          enableManualMode
          autoLoadThreshold={5}
          isLoading={isLoading}
          isFetching={isFetching}
          showEndOfList
          endOfListText="🎉 You've reached the end!"
        />
      )}
    </div>
  );
};

export default BaseSimilarProducts;
